/**
 * Client-side mirror of APIv2's ExperimentAnalysisSpecValidator pre-launch
 * measurement checks (Monday 12761663260). The server is authoritative —
 * this mirror exists so the operator sees failures and warnings before the
 * request round-trips, and so warnings can be acknowledged in the start
 * dialog (which resubmits with acknowledge_spec_warnings=true).
 *
 * Each rule encodes a defect class that shipped with alternate_onboarding:
 *   #1 a dismissal used as a completion marker without a property filter
 *      (in metric event fields, guardrail event fields, OR guardrail
 *      definition prose — the Aug 4 defect lived in guardrail prose)
 *   #2 arm attribution via the "[Experiment] ..." user property
 *   #3 a stop rule that never said which assignment counter it reads
 * plus a baseline rate quoted without the denominator it was measured on.
 */

export const STOP_RULE_COUNTER_SOURCES = [
  'amplitude_exposure_uniques',
  'framework_total_assigned',
];

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

// "Screen Dismissed (Upgrade Btn Clicked = true)" -> "Screen Dismissed"
const baseEventName = (name) => name.replace(/\s*\([^)]*\)\s*$/, '').trim();

// A dismissal-shaped marker with no TRAILING parenthesized property filter
// (the only accepted shape — a mid-name "(v2)" version tag is not a filter).
const isBareDismissalMarker = (eventName) => /dismiss/i.test(eventName) && !/\([^)]*\)\s*$/.test(eventName);

// primary_metric plus secondary_metrics, keyed by a label usable in messages.
function metrics(spec) {
  const out = [];
  if (spec.primary_metric && typeof spec.primary_metric === 'object') {
    out.push({ label: 'primary metric', metric: spec.primary_metric });
  }
  if (Array.isArray(spec.secondary_metrics)) {
    spec.secondary_metrics.forEach((metric, index) => {
      if (metric && typeof metric === 'object') {
        out.push({ label: `secondary metric ${index + 1}`, metric });
      }
    });
  }
  return out;
}

function guardrailRows(spec) {
  if (!Array.isArray(spec.guardrails)) return [];
  return spec.guardrails
    .map((guardrail, index) => ({ guardrail, index }))
    .filter(({ guardrail }) => guardrail && typeof guardrail === 'object')
    .map(({ guardrail, index }) => ({
      guardrail,
      name: isNonEmptyString(guardrail.name) ? guardrail.name : `guardrail ${index + 1}`,
    }));
}

// The metric-side event fields that must each match a declared Amplitude
// event: metric numerators/denominators plus the baseline denominator.
// Raw values — they may carry prose qualifiers.
function metricEventFields(spec) {
  const fields = new Set();
  metrics(spec).forEach(({ metric }) => {
    ['numerator_event', 'denominator_event'].forEach((field) => {
      if (isNonEmptyString(metric[field])) fields.add(metric[field].trim());
    });
  });
  if (isNonEmptyString(spec.baseline?.denominator_event)) {
    fields.add(spec.baseline.denominator_event.trim());
  }
  return [...fields];
}

// Every text in the spec that can carry an event name — event fields of
// metrics and guardrails, the baseline denominator, and definition prose
// (guardrails name their events inside definitions).
function eventBearingTexts(spec) {
  const texts = metricEventFields(spec);
  metrics(spec).forEach(({ metric }) => {
    if (isNonEmptyString(metric.definition)) texts.push(metric.definition);
  });
  guardrailRows(spec).forEach(({ guardrail }) => {
    ['numerator_event', 'denominator_event', 'definition'].forEach((field) => {
      if (isNonEmptyString(guardrail[field])) texts.push(guardrail[field]);
    });
  });
  return texts;
}

/**
 * Checks that invalidate measurement outright. Non-empty result blocks the
 * running transition; failures can never be acknowledged away.
 */
export function preLaunchFailures(spec) {
  const s = spec || {};
  const failures = [];

  metrics(s).forEach(({ label, metric }) => {
    const numerator = metric.numerator_event;
    if (isNonEmptyString(numerator) && isBareDismissalMarker(numerator)) {
      failures.push(
        `${label} numerator event "${numerator}" is a dismissal-shaped completion marker with no ` +
          'property filter — users who convert on an earlier screen never fire it; add a property ' +
          'qualifier, e.g. "... Dismissed (Upgrade Btn Clicked = true)"'
      );
    }
  });

  // Guardrails get the same scrutiny — the Aug 4 defect lived in a
  // guardrail: as prose in its definition, not a structured event field.
  guardrailRows(s).forEach(({ guardrail, name }) => {
    ['numerator_event', 'denominator_event'].forEach((field) => {
      const value = guardrail[field];
      if (isNonEmptyString(value) && isBareDismissalMarker(value)) {
        failures.push(
          `guardrail ${name} event "${value}" is a dismissal-shaped marker with no property filter — ` +
            'users who convert on an earlier screen never fire it; add a property qualifier'
        );
      }
    });
    const definition = guardrail.definition;
    if (typeof definition === 'string') {
      // An event-shaped phrase (TitleCase run) ending in "Dismissed" with
      // no trailing qualifier — ordinary prose like "users who dismissed
      // the paywall" doesn't trip this.
      const match = definition.match(/((?:[A-Z][A-Za-z0-9]*\s+)+Dismissed)(?!\s*\()/);
      if (match) {
        failures.push(
          `guardrail ${name} definition references the dismissal event "${match[1]}" with no property ` +
            'filter — a dismissal used as a completion/drop-off marker scores users who acted on an ' +
            'earlier screen as non-converters; qualify it, e.g. "... Dismissed (Upgrade Btn Clicked = true)"'
        );
      }
    }
  });

  const assignmentKey = s.attribution?.assignment_key;
  if (!isNonEmptyString(assignmentKey)) {
    failures.push('attribution needs the exposure-event property that assigns the arm (assignment key)');
  } else if (assignmentKey.toLowerCase().includes('[experiment]')) {
    // Substring, not an anchored regex — a copy-pasted leading non-breaking
    // space must not evade the check. Mirrors the server.
    failures.push(
      'the assignment key must not be an "[Experiment] ..." user property — user-property coverage ' +
        "is not guaranteed at exposure time; use the exposure event's variant property instead"
    );
  }

  if (!STOP_RULE_COUNTER_SOURCES.includes(s.stop_rule?.counter_source)) {
    failures.push(
      'the stop rule must declare which counter it reads: ' + STOP_RULE_COUNTER_SOURCES.join(' or ')
    );
  }

  // A % figure only counts as a quoted baseline near the word "baseline" —
  // power/alpha percentages ("80% power") must not trip this.
  const rationale = s.sample_size_rationale;
  const quotesBaselinePercent =
    typeof rationale === 'string' &&
    /baseline[^%]{0,80}?\d+(?:\.\d+)?\s*%|\d+(?:\.\d+)?\s*%[^%]{0,40}?baseline/i.test(rationale);
  const hasBaselineRate =
    s.baseline && typeof s.baseline === 'object' && Object.prototype.hasOwnProperty.call(s.baseline, 'rate');
  if ((quotesBaselinePercent || hasBaselineRate) && !isNonEmptyString(s.baseline?.denominator_event)) {
    failures.push(
      'the spec quotes a baseline rate but the baseline denominator event does not name the ' +
        'denominator it was measured on — without it the stated MDE is unverifiable'
    );
  }

  return failures;
}

/**
 * Advisory checks: they block the running transition only until the
 * operator explicitly acknowledges them. `assignmentMode` is the
 * experiment's assignment_mode (null when unknown) — used only for the
 * lagging-counter check.
 */
export function preLaunchWarnings(spec, { assignmentMode = null } = {}) {
  const s = spec || {};
  const warnings = [];

  metrics(s).forEach(({ label, metric }) => {
    if (!isNonEmptyString(metric.definition)) warnings.push(`${label} has no definition`);
  });
  guardrailRows(s).forEach(({ guardrail, name }) => {
    if (!isNonEmptyString(guardrail.definition)) warnings.push(`guardrail ${name} has no definition`);
  });

  // Cross-reference the declared Amplitude event list against the events
  // the spec actually uses. Matching is deliberately loose — substring
  // containment against every event field AND definition text: metric
  // fields carry prose qualifiers of arbitrary shape and guardrails name
  // their events inside prose, so exact-name matching flagged every event
  // on a good spec and buried the one warning that mattered.
  const listed = [
    ...new Set(
      (Array.isArray(s.amplitude_event_names) ? s.amplitude_event_names : [])
        .filter(isNonEmptyString)
        .map(baseEventName)
    ),
  ];
  const haystack = eventBearingTexts(s).join('\n');
  listed.forEach((declaredName) => {
    if (!haystack.includes(declaredName)) {
      warnings.push(
        `amplitude event names list "${declaredName}" but nothing in the spec references it (typo or leftover?)`
      );
    }
  });
  metricEventFields(s).forEach((fieldValue) => {
    if (!listed.some((declaredName) => fieldValue.includes(declaredName))) {
      warnings.push(
        `metric event "${fieldValue}" matches nothing in the Amplitude event names — its existence in ` +
          'the Amplitude taxonomy is unverified'
      );
    }
  });

  const baselineDenominator = s.baseline?.denominator_event;
  const primaryDenominator = s.primary_metric?.denominator_event;
  if (
    isNonEmptyString(baselineDenominator) &&
    isNonEmptyString(primaryDenominator) &&
    baseEventName(baselineDenominator) !== baseEventName(primaryDenominator)
  ) {
    warnings.push(
      `the baseline denominator "${baselineDenominator}" differs from the primary metric's ` +
        `denominator "${primaryDenominator}" — same funnel, different starting line; the stated ` +
        'MDE may be misleading'
    );
  }

  // For client_reported experiments, total_assigned is reported by clients
  // and structurally lags real exposures — a stop rule reading it
  // under-counts. Legitimate for server-assigned experiments.
  if (assignmentMode === 'client_reported' && s.stop_rule?.counter_source === 'framework_total_assigned') {
    warnings.push(
      'the stop counter framework_total_assigned structurally lags for client-reported experiments ' +
        '(clients report assignments late); prefer amplitude_exposure_uniques'
    );
  }

  return warnings;
}
